import { useQuery } from "@tanstack/react-query";
import { useParams, Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExclamationTriangle,
  faShieldAlt,
  faVirusSlash,
  faBug,
  faRadiation,
  faCalendarAlt,
  faArrowLeft,
} from "@fortawesome/free-solid-svg-icons";
import PageHeader from "../../componentes/PageHeader";
import GradientBanner from "../../componentes/Banner";
import MetaTags from "../../componentes/MetaTags";
import { FullscreenSpinner } from "../../componentes/LoadingIndicator";
import { fetchAlertDetails } from "../../api/alerts";
import { formatDate } from "../../utils/dateFormatter";
import "./ckeditor-content.css";
import "../../styles/circuit-background.css";

const defaultImage = "https://via.placeholder.com/800x400/e5e7eb/9ca3af?text=Alert+Image";
const backgroundIcons = [
  faExclamationTriangle,
  faShieldAlt,
  faVirusSlash,
  faBug,
  faRadiation,
];

const AlertDetails = () => {
  const { slug } = useParams(); 
  const { t, i18n } = useTranslation();
  const lang = useSelector((state) => state.language.selectedLanguage);

  // Fetch alert details using useQuery
  const {
    data: alert,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["alertDetails", slug, lang], 
    queryFn: () => fetchAlertDetails(slug, lang), 
  });

  const severityLabel =
    t(`alerts.severities.${alert?.severity}`) || "Unknown Severity";

  const getSeverityClass = (severity) => {
    switch (severity) {
      case "low":
        return "bg-green-500";
      case "medium":
        return "bg-yellow-500";
      case "high":
        return "bg-orange-500";
      case "critical":
        return "bg-red-500";
      default:
        return "bg-gray-300";
    }
  };

  if (isLoading) {
    return (
      <>
        <PageHeader />
        <GradientBanner
          title={t("alerts.loading")}
          description=""
          path={[
            { label: t("header.home"), link: "/" },
            { label: t("alerts.title"), link: "/alerts" },
            { label: t("alerts.loading"), link: null },
          ]}
        />
        <FullscreenSpinner />
      </>
    );
  }

  if (error) {
    return (
      <>
        <PageHeader />
        <GradientBanner
          title={t("alerts.error")}
          description={t("alerts.error_description") || "Something went wrong"}
          path={[
            { label: t("header.home"), link: "/" },
            { label: t("alerts.title"), link: "/alerts" },
            { label: t("alerts.error"), link: null },
          ]}
        />
        <div className="flex justify-center items-center min-h-[400px]">
          <p className="text-red-500">{error.message}</p>
        </div>
      </>
    );
  }

  if (!alert) {
    return (
      <>
        <PageHeader />
        <GradientBanner
          title={t("alerts.not_found")}
          description={t("alerts.not_found_description") || "Alert not found"}
          path={[
            { label: t("header.home"), link: "/" },
            { label: t("alerts.title"), link: "/alerts" },
            { label: t("alerts.not_found"), link: null },
          ]}
        />
        <div className="flex justify-center items-center min-h-[400px]">
          <p className="text-gray-500">
            {t("alerts.not_found_message") ||
              "The requested alert could not be found."}
          </p>
        </div>
      </>
    );
  }

  return (
    <>
      <MetaTags 
        title={`${alert.title || 'Alert'} — Ethio-CERT`} 
        description={alert.description ? alert.description.substring(0, 160) : 'Security alert from Ethio-CERT'} 
        url={`https://ethiocert.gov.et/alerts/${slug}`} 
      />
      <PageHeader />
      <div className="max-xl:pl-4 bg-gradient-to-r from-midnigt to-shake mt-[72px] pt-8 sm:pt-8 xl:pt-8 px-dynamic min-h-[14rem] md:min-h-[15rem] relative overflow-hidden">
        {/* Animated background elements */}
        {backgroundIcons.length > 0 && (
          <div className="absolute inset-0 overflow-hidden">
            {[...Array(20)].map((_, i) => (
              <div
                key={i}
                className="absolute animate-float"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 5}s`,
                  opacity: 0.1,
                }}
              >
                <FontAwesomeIcon
                  icon={backgroundIcons[i % backgroundIcons.length]}
                  className="text-white text-4xl"
                />
              </div>
            ))}
          </div>
        )}

        <div className="relative z-10">
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="flex items-center gap-2 text-white/80 text-sm md:mb-8 mb-6 max-xl:mt-8"
          >
            <Link to="/" className="hover:text-white transition-colors">
              {t("header.home")}
            </Link>
            <span>/</span>
            <Link to="/alerts" className="hover:text-white transition-colors">
              {t("alerts.title")}
            </Link>
            <span>/</span>
            <span className="break-words">
              {alert.title || t("alerts.title")}
            </span>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <motion.h1
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="text-h-5 font-bold text-white tracking-tight font-catamaran break-words max-w-[90%] md:max-w-[80%] mb-4 md:mb-6"
            >
              {alert.title}
            </motion.h1>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.6 }}
              className="flex w-fit rounded-lg border border-white/20 overflow-hidden shadow-lg"
            >
              <div
                className={`w-12 md:w-16 ${getSeverityClass(
                  alert.severity
                )} flex items-center justify-center`}
              >
                <FontAwesomeIcon
                  icon={faExclamationTriangle}
                  className="text-white text-lg md:text-2xl"
                />
              </div>
              <div className="w-fit text-white px-3 md:px-4 py-2 md:py-3">
                <p className="hidden xl:block text-body font-nunito-sans">
                  {t("alerts.status")}
                </p>
                <p className="text-base md:text-lg font-medium">
                  {severityLabel}
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      <div className="min-h-screen bg-gray-50">
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 py-8">
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="xl:col-span-2">
              <motion.article
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="bg-white rounded-2xl shadow-lg overflow-hidden"
              >
                {/* Article Header */}
                <div className="p-8 pb-6">
                  <div className="flex items-center gap-4 mb-6">
                    <span className="flex items-center text-sm text-gray-500 font-nunito-sans">
                      <FontAwesomeIcon icon={faCalendarAlt} className="w-4 h-4 mr-2 text-primary" />
                      {formatDate(alert.created_at, lang)}
                    </span>
                    <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium text-white ${getSeverityClass(alert.severity)}`}>
                      <FontAwesomeIcon icon={faExclamationTriangle} className="w-3 h-3" />
                      {severityLabel}
                    </div>
                  </div>
                  
                </div>

                {/* Image Section */}
                {alert.image && (
                  <div className="px-6 lg:px-8 mb-8">
                    <div className="aspect-video overflow-hidden rounded-xl shadow-lg">
                      <img
                        src={alert.image}
                        alt={alert.title || "Alert image"}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.src = defaultImage;
                        }}
                      />
                    </div>
                  </div>
                )}

                {/* Article Content */}
                <div className="px-6 lg:px-8 pb-8">
                  {/* Language fallback notice */}
                  {alert.is_fallback && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-xl flex items-start gap-3"
                    >
                      <FontAwesomeIcon
                        icon={faInfoCircle}
                        className="text-blue-500 mt-1 flex-shrink-0"
                      />
                      <p className="text-blue-800 text-sm font-nunito-sans">
                        {t("alerts.language_fallback_notice", {
                          requested: i18n.language,
                          actual: alert.actual_language,
                        })}
                      </p>
                    </motion.div>
                  )}

                  <div
                    dangerouslySetInnerHTML={{
                      __html:
                        alert.description ||
                        t("alerts.no_description") ||
                        "No Description",
                    }}
                    className="rich-content"
                  />
                </div>
              </motion.article>
            </div>

            {/* Sidebar */}
            <div className="xl:col-span-1">
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="bg-white rounded-2xl shadow-lg p-6 sticky top-8"
              >


                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <FontAwesomeIcon icon={faCalendarAlt} className="text-primary w-4 h-4" />
                    </div>
                    <div>
                      <h4 className="font-nunito-sans font-semibold text-gray-900 mb-1">
                        {t("alerts.first_published")}
                      </h4>
                      <p className="font-nunito-sans text-sm text-gray-600">
                        {formatDate(alert.created_at, lang)}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <FontAwesomeIcon icon={faCalendarAlt} className="text-primary w-4 h-4" />
                    </div>
                    <div>
                      <h4 className="font-nunito-sans font-semibold text-gray-900 mb-1">
                        {t("alerts.last_updated")}
                      </h4>
                      <p className="font-nunito-sans text-sm text-gray-600">
                        {formatDate(alert.updated_at || alert.created_at, lang)}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <FontAwesomeIcon icon={faExclamationTriangle} className="text-primary w-4 h-4" />
                    </div>
                    <div>
                      <h4 className="font-nunito-sans font-semibold text-gray-900 mb-1">
                        Severity Level
                      </h4>
                      <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold ${getSeverityClass(alert.severity)} text-white`}>
                        <FontAwesomeIcon icon={faExclamationTriangle} className="w-3 h-3" />
                        {severityLabel}
                      </div>
                    </div>
                  </div>
                </div>


              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AlertDetails;